import { Error } from '@app/core/models';
import { isNotNullish } from '@app/core/utils/types';
import { ObjectValidator, Validators } from '@app/core/utils/validators';
import { LastMileType, ParcelDimensions } from '@app/ecsomag/models';
import { parcelDestinationHasIsoCode, ParcelEntity } from '@app/ecsomag/parcel/store/order/model';
import { calculatePackageSize } from '@app/home/utils/parcel';
import { memoize, pick } from 'lodash-es';
import { FirstMileType } from '../ecsomag/models/order';
import { ParcelShop } from './parcel-shop';
import {
	ExtraService,
	ExtraServiceCalculatedProps,
	ExtraServiceId,
	ExtraServicesAdditionalDataKey,
	ExtraServicesPartialAdditionalDataMap,
	FALLBACK_PRICING_TYPE_ID,
	MutuallyExclusiveServices,
	PackageSize,
	PackageSizeName,
	PackageSizes,
	ParcelConstraints,
	parcelDimensionKeys,
	ParcelDiscount,
	PricingTypeId,
	ShippingCountry,
	ShippingCountryId,
	ShippingInfo,
	ShippingPricePerZone,
	ShippingPricesAPI,
	ShippingPriceVariants,
	ShippingZone,
	ShippingZoneId,
	UserType,
} from './prices';
import { ParcelValidatorsFactory } from './prices.validators';
import { User } from '../ecsomag/user/models';
import { ParcelSummary } from '../ecsomag/parcel/store/order/selectors';

export const transformShippingPrices = (shippingPricesAPI: ShippingPricesAPI): ShippingInfo => {
	const extraServicesRequiredFields: Record<ExtraServiceId, Pick<ExtraService, 'destinationTypes' | 'additionalData' | 'requiredFields' | 'relatedFields'>> = {
		1: {
			destinationTypes: ['address', 'parcel-shop', 'parcel-locker'],
			additionalData: ['cod'],
			// requiredFields: [ 'extraServices.additionalData.cod.amount', 'extraServices.additionalData.cod.bankAccountHolder', 'extraServices.additionalData.cod.bankAccountNumber' ],
		},
		2: {
			destinationTypes: ['address'],
			relatedFields: ['destination.deliveryAddress.homeDelivery.email', 'destination.deliveryAddress.homeDelivery.mobile'],
		},
		3: {
			destinationTypes: ['address'],
			relatedFields: ['destination.deliveryAddress.homeDelivery.email', 'destination.deliveryAddress.homeDelivery.mobile'],
		},
		4: {
			destinationTypes: ['address', 'parcel-shop', 'parcel-locker'],
			additionalData: ['cod'],
			// requiredFields: [ 'extraServices.additionalData.cod.amount', 'extraServices.additionalData.cod.bankAccountHolder', 'extraServices.additionalData.cod.bankAccountNumber' ],
		},
	};

	const constraints: Record<FirstMileType, ParcelConstraints> = {
		'pick-and-ship': {
			dimension: {
				length: { min: 15, max: 200 },
				width: { min: 10, max: 80 },
				height: { min: 1, max: 60 },
			},
			circumference: { min: 35, max: 300 },
		},
		'parcel-shop': {
			dimension: {
				length: { min: 15, max: 200 },
				width: { min: 10, max: 80 },
				height: { min: 1, max: 60 },
			},
			circumference: { min: 35, max: 300 },
		},
		'parcel-locker': {
			dimension: {
				length: { min: 15, max: 60 },
				width: { min: 10, max: 74 },
				height: { min: 1, max: 43 },
			},
			circumference: { min: 35, max: 280 },
		},
	};

	const parcelDiscounts: ParcelDiscount[] = [
		// {
		// 	id: '202309_A',
		// 	name: 'Kedvezmény',
		// 	description: 'GLS Automatában történő leadás esetén',
		// 	condition: 'discountCondition_202309_A',
		// 	operator: 'multiply',
		// 	amount: -0.25,
		// 	active: true,
		// },
		// {
		// 	id: '202210_A',
		// 	name: 'Kedvezmény',
		// 	description: 'GLS CsomagPonton és GLSAutomatában történő leadás esetén, címzett GLS Automatában veszi át',
		// 	condition: 'discountCondition_202210_A',
		// 	operator: 'add',
		// 	amount: -450,
		// 	active: true,
		// },
		// {
		// 	id: '202210_B',
		// 	name: 'Kedvezmény',
		// 	description: 'GLS CsomagPonton és GLSAutomatában történő leadás esetén, címzett GLS CsomagPonton veszi át',
		// 	condition: 'discountCondition_202210_B',
		// 	operator: 'add',
		// 	amount: -250,
		// 	active: true,
		// },
		// {
		// 	id: '202210_C',
		// 	name: 'Kedvezmény',
		// 	description: 'Címen futár által felvett csomagok esetén és GLS Automatában való kézbesítéskor',
		// 	condition: 'discountCondition_202210_C',
		// 	operator: 'add',
		// 	amount: -450,
		// 	active: true,
		// },
		// {
		// 	id: '202210_D',
		// 	name: 'Kedvezmény',
		// 	description: 'Címen futár által felvett csomagok esetén és GLS CsomagPontban való kézbesítéskor',
		// 	condition: 'discountCondition_202210_D',
		// 	operator: 'add',
		// 	amount: -250,
		// 	active: true,
		// },
	];

	const getMutuallyExclusiveServices = (extraServiceId: ExtraServiceId, mutuallyExclusiveServicesGlobal: MutuallyExclusiveServices[]) => {
		// console.log('selector selectShippingExtraServices');
		return mutuallyExclusiveServicesGlobal
			.filter(mes => mes.ids.includes(extraServiceId))
			.reduce((acc, mes) => {
				const ids = mes.ids.filter(id => id !== extraServiceId);
				acc.push(...ids);
				return acc;
			}, [] as ExtraServiceId[]);
	};

	return {
		zones: shippingPricesAPI.zones,
		countries: shippingPricesAPI.countries,
		pricingTypes: shippingPricesAPI.pricingTypes,
		packageSizes: shippingPricesAPI.packageSizes,
		extraServices: {
			...shippingPricesAPI.extraServices,
			services: shippingPricesAPI.extraServices.services.map(extraServiceAPI => ({
				...extraServiceAPI,
				destinationTypes: extraServiceAPI.destinationTypes ?? extraServicesRequiredFields[extraServiceAPI.id]?.destinationTypes ?? [],
				additionalData: extraServiceAPI.additionalData ?? extraServicesRequiredFields[extraServiceAPI.id]?.additionalData ?? [],
				requiredFields: extraServiceAPI.requiredFields ?? extraServicesRequiredFields[extraServiceAPI.id]?.requiredFields ?? [],
				relatedFields: extraServiceAPI.relatedFields ?? extraServicesRequiredFields[extraServiceAPI.id]?.relatedFields ?? [],
				mutuallyExclusiveServices: getMutuallyExclusiveServices(extraServiceAPI.id, shippingPricesAPI.extraServices.mutuallyExclusiveServices ?? []),
			})),
		},
		multipleDiscounts: shippingPricesAPI.multipleDiscounts,
		parcelDiscounts: shippingPricesAPI.parcelDiscounts ?? [],
		constraints,
	};
};

type DiscountConditionFn = ({
	parcel,
	destination,
	dimensions,
	parcelExtraServices,
}: {
	parcel: ParcelEntity & { firstMileType?: FirstMileType };
	destination: ParcelEntity['destination'];
	dimensions: ParcelEntity['dimensions'];
	parcelExtraServices: ParcelEntity['extraServices'];
}) => boolean;

const predefinedDiscountConditionsFactory = //memoize(

		(shippingInfo: ShippingInfo, parcelShops: ParcelShop[]) =>
		(condition: string): DiscountConditionFn => {
			console.log('prepare conditions');

			const parcelLockerIds = parcelShops.filter(ps => ps.features['gls-parcel-locker']).map(ps => ps.id);

			const prepareDiscountConditionParams = ({ parcel, destination }: { parcel: ParcelEntity & { firstMileType?: FirstMileType }; destination: ParcelEntity['destination'] }) => ({
				firstMileType: parcel.firstMileType,
				destinationIsoCode: destination.isoCode.toLowerCase(),
				destinationIsParcelLocker:
					parcelShops.length > 0 &&
					destination.type === 'parcel-locker' &&
					typeof destination.deliveryAddress?.parcelShop.parcelShopId === 'string' &&
					parcelLockerIds.includes(destination.deliveryAddress.parcelShop.parcelShopId),
				destinationIsParcelShop:
					parcelShops.length > 0 &&
					destination.type === 'parcel-shop' &&
					typeof destination.deliveryAddress?.parcelShop.parcelShopId === 'string' &&
					!parcelLockerIds.includes(destination.deliveryAddress.parcelShop.parcelShopId),
			});

			const predefinedDiscountConditionFns: Record<string, DiscountConditionFn> = {
				discountCondition_202309_A: args => {
					const discountConditionParams = prepareDiscountConditionParams(args);
					console.log(args, discountConditionParams);
					return (
						// discountConditionParams.destinationIsoCode === 'hu' &&
						discountConditionParams.firstMileType === 'parcel-locker' // &&
						// discountConditionParams.destinationIsParcelLocker
					);
				},

				// discountCondition_202210_A: args => {
				// 	const discountConditionParams = prepareDiscountConditionParams(args);
				// 	return (
				// 		discountConditionParams.destinationIsoCode === 'hu' &&
				// 		discountConditionParams.firstMileType === 'parcel-locker' &&
				// 		discountConditionParams.destinationIsParcelLocker
				// 	);
				// },
				// discountCondition_202210_B: args => {
				// 	const discountConditionParams = prepareDiscountConditionParams(args);
				// 	return (
				// 		discountConditionParams.destinationIsoCode === 'hu' &&
				// 		discountConditionParams.firstMileType === 'parcel-locker' &&
				// 		discountConditionParams.destinationIsParcelShop
				// 	);
				// },
				// discountCondition_202210_C: args => {
				// 	const discountConditionParams = prepareDiscountConditionParams(args);
				// 	return (
				// 		discountConditionParams.destinationIsoCode === 'hu' &&
				// 		discountConditionParams.firstMileType === 'pick-and-ship' &&
				// 		discountConditionParams.destinationIsParcelLocker
				// 	);
				// },
				// discountCondition_202210_D: args => {
				// 	const discountConditionParams = prepareDiscountConditionParams(args);
				// 	return (
				// 		discountConditionParams.destinationIsoCode === 'hu' &&
				// 		discountConditionParams.firstMileType === 'pick-and-ship' &&
				// 		discountConditionParams.destinationIsParcelShop
				// 	);
				// },
			};

			return predefinedDiscountConditionFns[condition] ?? (() => false);
		};
// );

export const getShippingCountryById = (shippingInfo: ShippingInfo, id?: ShippingCountryId) => shippingInfo.countries.find(country => country.id === id);

export const getShippingCountryByIsoCode = (shippingInfo: ShippingInfo, isoCode?: ShippingCountry['isoCode']) => shippingInfo.countries.find(country => country.isoCode === isoCode);

export const getShippingZoneById = (shippingInfo: ShippingInfo, zoneId?: ShippingZone['id']) => shippingInfo.zones.find(zone => zone.id === zoneId);

export const getPackageSizeByName = (shippingInfo: ShippingInfo, name?: PackageSizeName) => shippingInfo.packageSizes.find(packageSize => packageSize.size === name);

export const getExtraServiceById = (shippingInfo: ShippingInfo, id?: ExtraServiceId) => shippingInfo.extraServices.services.find(extraService => extraService.id === id);

export const getExtraServiceByAdditionalDataKey = (shippingInfo: ShippingInfo, additionalDataType: ExtraServicesAdditionalDataKey) =>
	shippingInfo.extraServices.services.find(extraService => extraService.additionalData?.includes(additionalDataType));

export const getParcelExtraServiceById = (parcelExtraServices: ParcelEntity['extraServices'], id?: ExtraServiceId) => parcelExtraServices.find(parcelExtraService => parcelExtraService.id === id);

export const reducePackageSizesToFiltered =
	({ zoneId: zoneIdFilter, pricingTypeId: pricingTypeIdFilter }: { zoneId?: ShippingZoneId; pricingTypeId?: PricingTypeId }) =>
	(filteredPackageSizes: PackageSize[], packageSize: PackageSize) => {
		const filteredPrices = packageSize.prices.filter(([zoneId, priceVariants]) => {
			if (isNotNullish(zoneIdFilter) && zoneId !== zoneIdFilter) return false;
			if (isNotNullish(pricingTypeIdFilter) && !Object.keys(priceVariants).includes(pricingTypeIdFilter)) {
				return false;
			}
			return true;
		});

		if (filteredPrices.length >= 1) {
			filteredPackageSizes.push({
				...packageSize,
				prices: filteredPrices.map(([zoneId, priceVariants]) => (pricingTypeIdFilter ? [zoneId, pick(priceVariants, pricingTypeIdFilter)] : [zoneId, priceVariants]) as ShippingPricePerZone),
			});
		}
		return filteredPackageSizes;
	};

const getPriceForPricingType = (
	prices: ShippingPriceVariants,
	{
		firstMileType = '*',
		lastMileType = '*',
		userType = '*',
		fallbackPricingTypeId = FALLBACK_PRICING_TYPE_ID,
	}: {
		firstMileType?: FirstMileType | '*';
		lastMileType?: LastMileType | '*';
		userType?: UserType | '*';
		fallbackPricingTypeId?: PricingTypeId;
	}
) => {
	const pricingTypeIds: Array<PricingTypeId> = [
		`${firstMileType}:${lastMileType}:${userType}`,
		`${firstMileType}:${lastMileType}:*`,
		`${firstMileType}:*:${userType}`,
		`*:${lastMileType}:${userType}`,
		`${firstMileType}:*:*`,
		`*:${lastMileType}:*`,
		`*:*:${userType}`,
		fallbackPricingTypeId,
	];
	console.log(prices, pricingTypeIds);
	for (const pricingTypeId of pricingTypeIds) {
		if (prices[pricingTypeId]) {
			console.log({ pricingTypeId, price: prices[pricingTypeId] });
			return { pricingTypeId, price: prices[pricingTypeId] };
		}
	}

	return undefined;
};

export const calculateDestination = (
	shippingInfo: ShippingInfo,
	{
		isoCode,
	}: {
		isoCode: ShippingCountry['isoCode'];
	}
) => {
	const destinationCountry = getShippingCountryByIsoCode(shippingInfo, isoCode);
	const zoneId = destinationCountry?.zoneId;
	const zone = getShippingZoneById(shippingInfo, zoneId);

	return {
		destinationCountry,
		zone,
	};
};

export const isParcelDimensions = (input: any): input is ParcelDimensions => {
	return parcelDimensionKeys.every(dimension => {
		return typeof input[dimension] === 'number';
	});
};

export const isShippingCountry = (input: any): input is ShippingCountry => {
	return typeof (input as ShippingCountry).isoCode === 'string';
};

export const calculateSize = (
	shippingInfo: ShippingInfo,
	{
		dimensions,
	}: {
		dimensions: ParcelDimensions;
	}
) => {
	const parcelSize = calculatePackageSize(shippingInfo, dimensions);

	return {
		parcelSize,
	};
};

export const calculateShippingPrice = ({ zone, parcelSize }: { zone: ShippingZone; parcelSize: PackageSize }) => {
	const shippingPrice = parcelSize.prices.find(packagePrice => packagePrice[0] === zone.id)?.[1];

	return shippingPrice;
};

export const calculateExtraServicePrice = (
	extraService: ExtraService,
	{
		additionalDataMap,
	}: {
		additionalDataMap: Partial<ExtraServicesPartialAdditionalDataMap>;
	}
): number | undefined => {
	console.log('calculateExtraServicePrice', extraService.shortName, extraService);
	if (extraService) {
		switch (extraService.priceType) {
			case 'simple': {
				return extraService.price;
			}
			case 'tiered': {
				const price = extraService.price.find(priceTier => {
					const [key, field] = priceTier.field.split('.', 2);
					if (key && field) {
						const additionalData = additionalDataMap[key as ExtraServicesAdditionalDataKey];
						if (additionalData) {
							const fieldValue = (additionalData as any)[field];
							if (isNotNullish(fieldValue)) {
								const fieldValueNum = Number(fieldValue);
								return fieldValueNum >= priceTier.min && fieldValueNum <= priceTier.max;
							}
						}
					}
					return false;
				})?.price;
				return price;
			}
		}
	}
	return undefined;
};

export const getExtraServiceDetails = (extraService: ExtraService, parcelExtraService?: ParcelEntity['extraServices'][0]) => {
	console.log('getExtraServiceDetails');
	const additionalData = parcelExtraService?.data ?? {};
	return {
		additionalDataValues: additionalData,
		effectivePrice: calculateExtraServicePrice(extraService, { additionalDataMap: additionalData }),
	};
};

export const getAllExtraServicesDetails = (
	shippingInfo: ShippingInfo,
	{
		parcelExtraServices = [],
		destination,
	}: {
		parcelExtraServices: ParcelEntity['extraServices'];
		destination: ParcelEntity['destination'];
	}
) => {
	console.log('getAllExtraServicesDetails');
	// const extraServices = shippingInfo.extraServices.services;

	const validators = getParcelValidators(shippingInfo);

	const extraServicesDetails = shippingInfo.extraServices.services.map(extraService => {
		const parcelExtraService = getParcelExtraServiceById(parcelExtraServices, extraService.id);

		const extraServiceValidators = {
			calculator: validators.extraServiceForCalculator[extraService.key],
			dispatch: validators.extraServiceForDispatch[extraService.key],
		};
		const validity = {
			calculator: {
				errors: extraServiceValidators.calculator.getErrors(parcelExtraService ?? {}),
				valid: extraServiceValidators.calculator.isValid(parcelExtraService ?? {}),
			},
			dispatch: {
				errors: extraServiceValidators.dispatch.getErrors(parcelExtraService ?? {}),
				valid: extraServiceValidators.dispatch.isValid(parcelExtraService ?? {}),
			},
		};

		const destinationCountry = getShippingCountryByIsoCode(shippingInfo, destination.isoCode);

		const available =
			(!extraService.countries?.length || (typeof destinationCountry !== 'undefined' ? extraService.countries.includes(destinationCountry.id) : false)) &&
			(!extraService.destinationTypes?.length || extraService.destinationTypes.includes(destination.type));
		const selected = parcelExtraService?.selected ?? false;

		const selectedExtraServiceDetails = getExtraServiceDetails(extraService, parcelExtraService);

		// console.log(extraService, parcelExtraService, available, selected, selectedExtraServiceDetails, validity)

		// const errors: Error[] = []; // selectedExtraService ? validateSelectedExtraService(shippingInfo, { selectedExtraService }) : [];
		// const valid = errors.length === 0;

		// const errorsForCalculation: Error[] = []; // selectedExtraService ? validateSelectedExtraServiceForCalculation(shippingInfo, { selectedExtraService }) : [];
		// const validForCalculation = errorsForCalculation.length === 0;

		const result: ExtraService & ExtraServiceCalculatedProps = {
			...extraService,
			available,
			selected,
			...selectedExtraServiceDetails,
			// errors,
			// valid,
			// errorsForCalculation,
			// validForCalculation,
			validity,
		};

		return result;
	});

	const extraServicesPriceTotal = extraServicesDetails
		.filter(extraService => extraService.selected && extraService.available)
		.reduce((acc: number, extraService) => (acc += extraService?.effectivePrice ?? 0), 0);
	return {
		extraServicesDetails,
		extraServicesPriceTotal,
	};
};

export const getParcelDetails = (
	{
		parcel,
		destination,
		dimensions,
		parcelExtraServices = [],
	}: {
		parcel: ParcelEntity & { firstMileType?: FirstMileType };
		destination: ParcelEntity['destination'];
		dimensions: ParcelEntity['dimensions'];
		parcelExtraServices: ParcelEntity['extraServices'];
	},
	ctx: {
		shippingInfo: ShippingInfo;
		parcelShops: ParcelShop[];
		auth: User | null | undefined;
	}
) => {
	console.groupCollapsed('getParcelDetails()');
	console.log('args', {
		parcel,
		destination,
		dimensions,
		parcelExtraServices,
	});
	console.log('ctx', ctx);
	const { shippingInfo, parcelShops, auth } = ctx;

	const destinationCountry = destination && parcelDestinationHasIsoCode(destination) ? getShippingCountryByIsoCode(shippingInfo, destination.isoCode) : undefined;
	const destinationCountryAndZone = destination && parcelDestinationHasIsoCode(destination) ? calculateDestination(shippingInfo, { isoCode: destination.isoCode }) : undefined;
	const size = isParcelDimensions(dimensions) ? calculateSize(shippingInfo, { dimensions }) : undefined;

	const shippingPriceVariants = destinationCountryAndZone?.zone && size?.parcelSize ? calculateShippingPrice({ zone: destinationCountryAndZone.zone, parcelSize: size.parcelSize }) : undefined;

	const shippingPrice = shippingPriceVariants
		? getPriceForPricingType(shippingPriceVariants, {
				firstMileType: parcel.firstMileType,
				lastMileType: destination.type,
				userType: isNotNullish(auth) ? 'registered' : 'guest',
		  })
		: undefined;

	const shippingPriceDetails = shippingPrice
		? {
				...shippingPrice,
				pricingType: shippingInfo.pricingTypes.find(pricingType => pricingType.id === shippingPrice?.pricingTypeId),
		  }
		: undefined;

	// const selectedExtraServicesDetails = selectedExtraServices ? getSelectedExtraServicesDetails( shippingInfo, { selectedExtraServices }) : undefined;
	const extraServicesDetails = getAllExtraServicesDetails(shippingInfo, { destination, parcelExtraServices });

	let total: number = 0;
	const discountDetails: {
		id: number;
		name: string;
		amount: number;
		discountBase: ParcelDiscount['discountBase'];
	}[] = [];

	if (typeof shippingPrice?.price !== 'undefined') {
		total += shippingPrice.price;

		total += extraServicesDetails.extraServicesPriceTotal;

		// discounts

		// const parcelLockerIds = parcelShops.filter(ps => ps.features['gls-parcel-locker']).map(ps => ps.id);

		// const discountConditionParams = {
		// 	firstMileType: parcel.firstMileType,
		// 	destinationIsoCode: destination.isoCode.toLowerCase(),
		// 	destinationIsParcelLocker:
		// 		parcelShops.length > 0 &&
		// 		destination.type === 'parcelShop' &&
		// 		typeof destination.deliveryAddress?.parcelShop.parcelShopId === 'string' &&
		// 		parcelLockerIds.includes(destination.deliveryAddress.parcelShop.parcelShopId),
		// 	destinationIsParcelShop:
		// 		parcelShops.length > 0 &&
		// 		destination.type === 'parcelShop' &&
		// 		typeof destination.deliveryAddress?.parcelShop.parcelShopId === 'string' &&
		// 		!parcelLockerIds.includes(destination.deliveryAddress.parcelShop.parcelShopId),
		// };

		const parcelDiscounts = shippingInfo.parcelDiscounts;
		console.log('parcelDiscounts', parcelDiscounts);

		for (const { id, name, active, condition, conditions, discountBase, operator, amount } of parcelDiscounts) {
			if (!active) continue;
			// if (
			// 	typeof condition === 'string' &&
			// 	predefinedDiscountConditions(condition)({
			// 		parcel,
			// 		destination,
			// 		dimensions,
			// 		parcelExtraServices,
			// 	})
			// ) {
			// 	switch (operator) {
			// 		case 'add': {
			// 			discountDetails.push({ name, amount });
			// 			break;
			// 		}
			// 		case 'multiply': {
			// 			discountDetails.push({ name, amount: Math.round(shippingPriceDetails.shippingPrice * amount) });
			// 			break;
			// 		}
			// 	}
			// }

			console.log('check conditions', conditions);
			if (
				Object.keys(conditions).length === 0 ||
				(Object.keys(conditions) as Array<keyof typeof conditions>).every(condition => {
					switch (condition) {
						case 'authRequired': {
							console.log(condition, (auth !== null) === conditions['authRequired']);
							return (auth !== null) === conditions['authRequired'];
						}
					}
					return false;
				})
			) {
				switch (operator) {
					case 'add': {
						discountDetails.push({ id, name, amount, discountBase });
						break;
					}
					case 'multiply': {
						discountDetails.push({
							id,
							name,
							amount: Math.round((discountBase === 'base' ? shippingPrice.price : total) * amount),
							discountBase,
						});
						break;
					}
				}
			}
		}

		console.log('discountDetails', discountDetails);
		discountDetails.forEach(discount => {
			total += discount.amount;
		});
	}

	// validation
	const validators = getParcelValidators(shippingInfo);

	const validity = {
		dimensions: {
			calculator: {
				errors: validators.dimensionsForCalculator.getErrors(parcel),
				valid: validators.dimensionsForCalculator.isValid(parcel),
			},
			dispatch: {
				errors: validators.dimensionsForDispatch.getErrors(parcel),
				valid: validators.dimensionsForDispatch.isValid(parcel),
			},
			'pick-and-ship': {
				errors: validators.dimensionsForPickAndShip.getErrors(parcel),
				valid: validators.dimensionsForPickAndShip.isValid(parcel),
			},
			'parcel-shop': {
				errors: validators.dimensionsForParcelShop.getErrors(parcel),
				valid: validators.dimensionsForParcelShop.isValid(parcel),
			},
			'parcel-locker': {
				errors: validators.dimensionsForParcelLocker.getErrors(parcel),
				valid: validators.dimensionsForParcelLocker.isValid(parcel),
			},
		},
		destination: {
			calculator: {
				errors: validators.destinationForCalculator.getErrors(parcel),
				valid: validators.destinationForCalculator.isValid(parcel),
			},
			dispatch: {
				errors: validators.destinationForDispatch.getErrors(parcel),
				valid: validators.destinationForDispatch.isValid(parcel),
			},
		},
	};

	console.log(validity);

	console.log({
		discountsTotal: discountDetails.reduce((acc, d) => acc + d.amount, 0),

		total,
	});
	console.groupEnd();

	return {
		...destinationCountryAndZone,
		...size,
		shippingPriceDetails,
		...extraServicesDetails,
		discountDetails,
		discountsTotal: discountDetails.reduce((acc, d) => acc + d.amount, 0),

		total,

		validity,
	};
};
export type ParcelDetails = ReturnType<typeof getParcelDetails>;

export type ParcelSummaryTableRow = {
	name: string;
	info?: string;
	amount: number;
	price?: number;
	priceTotal?: number;
	error?: boolean;
} & (
	| {
			type: 'parcel';
			data: ParcelSummary;
	  }
	| {
			type: 'extraService';
			data: ExtraService;
	  }
	| {
			type: 'discount';
			data: Pick<ParcelDiscount, 'id' | 'discountBase'>;
	  }
	| {
			type: 'other';
			data?: any;
	  }
);

// export const getParcelValidity = (
// 	shippingInfo: ShippingInfo,
// 	{
// 		destinationCountry,
// 		dimensions,
// 		selectedExtraServices = [],
// 		deliveryAddress,
// 	} : {
// 		destinationCountry?: ShippingCountry,
// 		dimensions?: ParcelDimensions,
// 		selectedExtraServices?: SelectedExtraServices,
// 		deliveryAddress?: Parcel['destination']['deliveryAddress'],
// 	}
// ) => {
// 	console.groupCollapsed('getParcelValidity()');
// 	console.log('args', {
// 		destinationCountry,
// 		dimensions,
// 		selectedExtraServices,
// 		deliveryAddress,
// 	});

// 	const validity = {
// 		destination: validateDestinationCountry(shippingInfo, { destinationCountry }).length === 0,
// 		dimensions: validateSize(shippingInfo, { dimensions }).length === 0,
// 		selectedExtraServices: validateSelectedExtraServices(shippingInfo, { selectedExtraServices }).length === 0,
// 		selectedExtraServicesForCalculation: validateSelectedExtraServicesForCalculation(shippingInfo, { selectedExtraServices }).length === 0,
// 		deliveryAddress: validateDeliveryAddress(deliveryAddress).length === 0,
// 	};

// 	console.log(validity);
// 	console.groupEnd();

// 	return validity;
// };

export type ParcelValidatorMode = 'calculator' | 'dispatch';

const getValidatorConfigsForDestination = (shippingInfo: ShippingInfo) => {
	const destinationTypeCondition = (...types: LastMileType[]) => ({
		path: `destination.type`,
		predicate: (destinationType: LastMileType) => types.includes(destinationType),
	});

	const destinationIsoCodeCondition = (...isoCodes: string[]) => ({
		path: `destination.isoCode`,
		predicate: (destinationIsoCode: string) => isoCodes.map(s => s.toLowerCase()).includes(destinationIsoCode.toLowerCase()),
	});

	const destinationRequireFinalDeliveryAddressCondition = () => ({
		path: `destination`,
		predicate: ({ isoCode, type }: { isoCode: string; type: LastMileType }) => {
			if (type === 'address') return false;
			const country = shippingInfo.countries.find(c => c.isoCode === isoCode);
			if (country) return country.requireFinalDeliveryAddress;
			return false;
		},
	});

	return [
		// recipient.name
		{ path: 'destination.deliveryAddress.name', validators: [Validators.required] },

		// recipient.email (required only if flexdelivery is selected)
		{ path: 'destination.deliveryAddress.email', validators: [Validators.email] },
		{
			path: 'destination.deliveryAddress.email',
			validators: [Validators.required],
			opts: {
				condition: {
					path: `extraServices.0.selected`,
					predicate: (selected: boolean) => selected,
				},
			},
		},

		// recipient.mobile
		{
			path: 'destination.deliveryAddress.mobile',
			validators: [Validators.myglsPhoneNumber('any')],
			opts: {
				condition: {
					path: `destination.isoCode`,
					predicate: (destinationIsoCode: string) => !(Validators.getMyglsPhoneNumberIsoCodes() as string[]).includes(destinationIsoCode?.toLowerCase()),
				},
			},
		},
		...Validators.getMyglsPhoneNumberIsoCodes().map(isoCode => ({
			path: `destination.deliveryAddress.mobile`,
			validators: [Validators.myglsPhoneNumber(isoCode)],
			opts: { condition: destinationIsoCodeCondition(isoCode) },
		})),

		// lastMileType === 'address'
		...['zipCode', 'city', 'street', 'houseNumber'].map(field => ({
			path: `destination.deliveryAddress.address.${field}`,
			validators: [Validators.required],
			opts: { condition: destinationTypeCondition('address') },
		})),
		{
			path: 'destination.deliveryAddress.address.zipCode',
			validators: [Validators.minLength(4), Validators.maxLength(4), Validators.zipCode],
			opts: {
				condition: destinationIsoCodeCondition('hu'),
			},
		},
		{ path: 'destination.deliveryAddress.address.houseNumber', validators: [Validators.houseNumber] },

		// lastMileType === 'parcel-locker'
		{
			path: `destination.deliveryAddress.parcelLocker.parcelShopId`,
			validators: [Validators.required],
			opts: { condition: destinationTypeCondition('parcel-locker') },
		},

		// lastMileType === 'parcel-shop'
		{
			path: `destination.deliveryAddress.parcelShop.parcelShopId`,
			validators: [Validators.required],
			opts: { condition: destinationTypeCondition('parcel-shop') },
		},

		// requireFinalDeliveryAddress
		...['zipCode', 'city', 'street', 'houseNumber'].map(field => ({
			path: `destination.finalDeliveryAddress.${field}`,
			validators: [Validators.required],
			opts: { condition: destinationRequireFinalDeliveryAddressCondition() },
		})),
	];
};

export const getParcelValidator = (shippingInfo: ShippingInfo, config: ParcelValidatorMode = 'calculator') => {
	const ParcelValidators = ParcelValidatorsFactory(shippingInfo);
	const validator = new ObjectValidator();

	const firstMileTypeCondition = (type: FirstMileType) => ({
		path: `firstMileType`,
		predicate: (firstMileType: FirstMileType) => firstMileType === type,
	});

	validator.add([
		{
			path: 'dimensions.length',
			validators: [ParcelValidators.dimension('length', 'pick-and-ship')],
			opts: { condition: firstMileTypeCondition('pick-and-ship') },
		},
		{
			path: 'dimensions.width',
			validators: [ParcelValidators.dimension('width', 'pick-and-ship')],
			opts: { condition: firstMileTypeCondition('pick-and-ship') },
		},
		{
			path: 'dimensions.height',
			validators: [ParcelValidators.dimension('height', 'pick-and-ship')],
			opts: { condition: firstMileTypeCondition('pick-and-ship') },
		},
		{
			path: 'dimensions',
			validators: [ParcelValidators.size('pick-and-ship')],
			opts: { condition: firstMileTypeCondition('pick-and-ship') },
		},

		{
			path: 'dimensions.length',
			validators: [ParcelValidators.dimension('length', 'parcel-shop')],
			opts: { condition: firstMileTypeCondition('parcel-shop') },
		},
		{
			path: 'dimensions.width',
			validators: [ParcelValidators.dimension('width', 'parcel-shop')],
			opts: { condition: firstMileTypeCondition('parcel-shop') },
		},
		{
			path: 'dimensions.height',
			validators: [ParcelValidators.dimension('height', 'parcel-shop')],
			opts: { condition: firstMileTypeCondition('parcel-shop') },
		},
		{
			path: 'dimensions',
			validators: [ParcelValidators.size('parcel-shop')],
			opts: { condition: firstMileTypeCondition('parcel-shop') },
		},

		{
			path: 'dimensions.length',
			validators: [ParcelValidators.dimension('length', 'parcel-locker')],
			opts: { condition: firstMileTypeCondition('parcel-locker') },
		},
		{
			path: 'dimensions.width',
			validators: [ParcelValidators.dimension('width', 'parcel-locker')],
			opts: { condition: firstMileTypeCondition('parcel-locker') },
		},
		{
			path: 'dimensions.height',
			validators: [ParcelValidators.dimension('height', 'parcel-locker')],
			opts: { condition: firstMileTypeCondition('parcel-locker') },
		},
		{
			path: 'dimensions',
			validators: [ParcelValidators.size('parcel-locker')],
			opts: { condition: firstMileTypeCondition('parcel-locker') },
		},

		{ path: 'destination.isoCode', validators: [Validators.required] },
		{ path: 'destination.type', validators: [Validators.required] },
	]);

	if (config === 'dispatch') {
		validator.add([...getValidatorConfigsForDestination(shippingInfo)]);
	}

	shippingInfo.extraServices.services?.forEach((service, i) => {
		if (service.additionalData?.includes('cod')) {
			const extraServiceSelectedCondition = {
				path: `extraServices.${i}.selected`,
				predicate: (selected: boolean) => selected,
			};
			validator.add([
				{
					path: `extraServices.${i}.data.cod.amount`,
					validators: [Validators._required({ code: `codAmount.required` }), Validators._number({ code: `codAmount.number` }), ParcelValidators.codAmount],
					opts: {
						condition: extraServiceSelectedCondition,
					},
				},
			]);
			if (config === 'dispatch') {
				validator.add([
					{
						path: `extraServices.${i}.data.cod.bankAccountNumber`,
						validators: [Validators.required, Validators.bankAccountNumber],
						opts: {
							condition: extraServiceSelectedCondition,
						},
					},
					{
						path: `extraServices.${i}.data.cod.bankAccountHolder`,
						validators: [Validators.required],
						opts: {
							condition: extraServiceSelectedCondition,
						},
					},
				]);
			}
		}
	});

	return validator;
};

export const getParcelDimensionsValidator = (
	shippingInfo: ShippingInfo,
	opts?: {
		firstMileType?: FirstMileType;
	}
) => {
	const ParcelValidators = ParcelValidatorsFactory(shippingInfo);
	const validator = new ObjectValidator();

	if (opts?.firstMileType) {
		validator.add([
			{ path: 'dimensions.length', validators: [ParcelValidators.dimension('length', opts.firstMileType)] },
			{ path: 'dimensions.width', validators: [ParcelValidators.dimension('width', opts.firstMileType)] },
			{ path: 'dimensions.height', validators: [ParcelValidators.dimension('height', opts.firstMileType)] },
			{ path: 'dimensions', validators: [ParcelValidators.size(opts.firstMileType)] },
		]);
	} else {
		const firstMileTypeCondition = (type: FirstMileType) => ({
			path: `firstMileType`,
			predicate: (firstMileType: FirstMileType) => firstMileType === type,
		});

		validator.add([
			{
				path: 'dimensions.length',
				validators: [ParcelValidators.dimension('length', 'pick-and-ship')],
				opts: { condition: firstMileTypeCondition('pick-and-ship') },
			},
			{
				path: 'dimensions.width',
				validators: [ParcelValidators.dimension('width', 'pick-and-ship')],
				opts: { condition: firstMileTypeCondition('pick-and-ship') },
			},
			{
				path: 'dimensions.height',
				validators: [ParcelValidators.dimension('height', 'pick-and-ship')],
				opts: { condition: firstMileTypeCondition('pick-and-ship') },
			},
			{
				path: 'dimensions',
				validators: [ParcelValidators.size('pick-and-ship')],
				opts: { condition: firstMileTypeCondition('pick-and-ship') },
			},

			{
				path: 'dimensions.length',
				validators: [ParcelValidators.dimension('length', 'parcel-shop')],
				opts: { condition: firstMileTypeCondition('parcel-shop') },
			},
			{
				path: 'dimensions.width',
				validators: [ParcelValidators.dimension('width', 'parcel-shop')],
				opts: { condition: firstMileTypeCondition('parcel-shop') },
			},
			{
				path: 'dimensions.height',
				validators: [ParcelValidators.dimension('height', 'parcel-shop')],
				opts: { condition: firstMileTypeCondition('parcel-shop') },
			},
			{
				path: 'dimensions',
				validators: [ParcelValidators.size('parcel-shop')],
				opts: { condition: firstMileTypeCondition('parcel-shop') },
			},

			{
				path: 'dimensions.length',
				validators: [ParcelValidators.dimension('length', 'parcel-locker')],
				opts: { condition: firstMileTypeCondition('parcel-locker') },
			},
			{
				path: 'dimensions.width',
				validators: [ParcelValidators.dimension('width', 'parcel-locker')],
				opts: { condition: firstMileTypeCondition('parcel-locker') },
			},
			{
				path: 'dimensions.height',
				validators: [ParcelValidators.dimension('height', 'parcel-locker')],
				opts: { condition: firstMileTypeCondition('parcel-locker') },
			},
			{
				path: 'dimensions',
				validators: [ParcelValidators.size('parcel-locker')],
				opts: { condition: firstMileTypeCondition('parcel-locker') },
			},
		]);
	}

	return validator;
};

export const getParcelExtraServicesValidator = (
	shippingInfo: ShippingInfo,
	opts: {
		mode: ParcelValidatorMode;
	} = {
		mode: 'calculator',
	}
) => {
	const ParcelValidators = ParcelValidatorsFactory(shippingInfo);
	const validator = new ObjectValidator();

	shippingInfo.extraServices.services?.forEach((service, i) => {
		if (service.additionalData?.includes('cod')) {
			const extraServiceSelectedCondition = {
				path: `extraServices.${i}.selected`,
				predicate: (selected: boolean) => selected,
			};
			validator.add([
				{
					path: `extraServices.${i}.data.cod.amount`,
					validators: [Validators._required({ code: `codAmount.required` }), Validators._number({ code: `codAmount.number` }), ParcelValidators.codAmount],
					opts: {
						condition: extraServiceSelectedCondition,
					},
				},
			]);
			if (opts.mode === 'dispatch') {
				validator.add([
					{
						path: `extraServices.${i}.data.cod.bankAccountNumber`,
						validators: [Validators.required, Validators.bankAccountNumber],
						opts: {
							condition: extraServiceSelectedCondition,
						},
					},
					{
						path: `extraServices.${i}.data.cod.bankAccountHolder`,
						validators: [Validators.required],
						opts: {
							condition: extraServiceSelectedCondition,
						},
					},
				]);
			}
		}
	});

	return validator;
};

export const getParcelExtraServiceValidator = (
	shippingInfo: ShippingInfo,
	extraServiceId: ExtraServiceId,
	opts: {
		mode: ParcelValidatorMode;
	} = {
		mode: 'calculator',
	}
) => {
	const ParcelValidators = ParcelValidatorsFactory(shippingInfo);
	const validator = new ObjectValidator();

	const serviceInfo = shippingInfo.extraServices.services?.find(service => service.id === extraServiceId);

	if (serviceInfo?.additionalData) {
		if (serviceInfo.additionalData?.includes('cod')) {
			const extraServiceSelectedCondition = {
				path: `selected`,
				predicate: (selected: boolean) => selected,
			};
			validator.add([
				{
					path: `data.cod.amount`,
					validators: [Validators._required({ code: `codAmount.required` }), Validators._number({ code: `codAmount.number` }), ParcelValidators.codAmount],
					opts: {
						condition: extraServiceSelectedCondition,
					},
				},
			]);
			if (opts.mode === 'dispatch') {
				validator.add([
					{
						path: `data.cod.bankAccountNumber`,
						validators: [Validators.required, Validators.bankAccountNumber],
						opts: {
							condition: extraServiceSelectedCondition,
						},
					},
					{
						path: `data.cod.bankAccountHolder`,
						validators: [Validators.required],
						opts: {
							condition: extraServiceSelectedCondition,
						},
					},
				]);
			}
		}
	}

	return validator;
};

export const getParcelDestinationValidator = (
	shippingInfo: ShippingInfo,
	opts: {
		mode: ParcelValidatorMode;
	} = {
		mode: 'calculator',
	}
) => {
	const ParcelValidators = ParcelValidatorsFactory(shippingInfo);
	const validator = new ObjectValidator();

	validator.add([
		{ path: 'destination.isoCode', validators: [Validators.required] },
		{ path: 'destination.type', validators: [Validators.required] },
	]);

	if (opts.mode === 'dispatch') {
		validator.add([...getValidatorConfigsForDestination(shippingInfo)]);
	}

	return validator;
};

export const getParcelValidators = (shippingInfo: ShippingInfo) => {
	return {
		dimensionsForPickAndShip: getParcelDimensionsValidator(shippingInfo, { firstMileType: 'pick-and-ship' }),
		dimensionsForParcelShop: getParcelDimensionsValidator(shippingInfo, { firstMileType: 'parcel-shop' }),
		dimensionsForParcelLocker: getParcelDimensionsValidator(shippingInfo, { firstMileType: 'parcel-locker' }),
		dimensionsForCalculator: getParcelDimensionsValidator(shippingInfo),
		dimensionsForDispatch: getParcelDimensionsValidator(shippingInfo, { firstMileType: 'pick-and-ship' }),
		destinationForCalculator: getParcelDestinationValidator(shippingInfo, { mode: 'calculator' }),
		destinationForDispatch: getParcelDestinationValidator(shippingInfo, { mode: 'dispatch' }),
		extraServicesForCalculator: getParcelExtraServicesValidator(shippingInfo, { mode: 'calculator' }),
		extraServicesForDispatch: getParcelExtraServicesValidator(shippingInfo, { mode: 'dispatch' }),
		extraServiceForCalculator: shippingInfo.extraServices.services.reduce((acc, extraService) => {
			acc[extraService.key] = getParcelExtraServiceValidator(shippingInfo, extraService.id, {
				mode: 'calculator',
			});
			return acc;
		}, {} as Record<ExtraService['key'], ObjectValidator>),
		extraServiceForDispatch: shippingInfo.extraServices.services.reduce((acc, extraService) => {
			acc[extraService.key] = getParcelExtraServiceValidator(shippingInfo, extraService.id, { mode: 'dispatch' });
			return acc;
		}, {} as Record<ExtraService['key'], ObjectValidator>),
	};
};

// export const getParcelErrors = (
// 	shippingInfo: ShippingInfo,
// 	parcel: ParcelEntity,
// 	config: 'calculator' | 'dispatch' = 'calculator',
// ) => {
// 	const validator = getParcelValidator(shippingInfo, config);

// 	console.groupCollapsed('getParcelErrors()');
// 	console.log('args', parcel);

// 	console.log('validator', validator);

// 	const errors = validator.validate(parcel);
// 	console.log('errors', errors);

// 	console.groupEnd();

// 	return errors;
// };
